/**
 * A project tab's views besides Lanes (navigation-ia.md §3.2 Phase 2, artboards `optimal/`
 * decision 3). They reuse the classic Projects page's bodies, now inside the tab's chrome:
 *
 *   /p/$id/history    History (sessions + transcript search) · Changes · Outside Viko imports
 *   /p/$id/releases   Builds: release lanes and builds for this project · Landings
 *   /p/$id/artifacts  Artifacts · Project research · Feedback
 *   /p/$id/settings   Checkouts · configuration, loop, autonomy, inventory, feedback sources,
 *                     sources (integrations) · project and workstream rules
 */

import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import { FileCog, Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { ArtifactGallery } from '../components/Artifacts.js'
import { ProjectChangelog } from '../components/Changelog.js'
import { CallHistory } from '../components/call/CallHistory.js' // viko-call
import { FeedbackQueue, FeedbackSettings } from '../components/Feedback.js'
import { ImportReview, IngestSources } from '../components/Ingest.js'
import { LandGrantCard } from '../components/LandGrant.js'
import { ProjectCheckouts } from '../components/ProjectCheckouts.js'
import { ProjectConfigSheet } from '../components/ProjectConfigSheet.js'
import { ProjectInventory } from '../components/ProjectInventory.js'
import { AutonomyEditor, HistoryConsent, LoopPicker } from '../components/ProjectSettings.js'
import { ResearchLibrary } from '../components/research/ResearchLibrary.js'
import { useBackend } from '../lib/backend.js'
import { settingsHref } from '../lib/ia.js'
import { qk } from '../lib/queries.js'
import type { Project } from '../lib/types.js'
import { ProjectBuilds } from './project-builds.js'
import './project-views.css'
import { ProjectSessions } from './project-history.js'
import { useTabProject } from './project-tab.js'
import { NewWorkstream } from './projects.js'
import { RulesPage } from './rules.js'

function View({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="lk-view-body" role="region" aria-label={label}>
      {children}
    </div>
  )
}

/** A section: 15 px semibold head (+ muted count or aside), then hairline rows. */
function Part({
  title,
  children,
  id,
  count,
  aside,
}: {
  title: string
  children: ReactNode
  id: string
  count?: ReactNode
  aside?: ReactNode
}) {
  return (
    <section className="pv-sec" aria-labelledby={id}>
      <div className="pv-head">
        <h2 id={id} className="pv-h">
          {title}
          {count !== undefined ? <span className="pv-h__count">{count}</span> : null}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  )
}

export function ProjectHistoryView() {
  const p = useTabProject()
  return (
    <View label={`${p.name} history`}>
      <div className="pv-body">
        <ProjectSessions project={p} />
        <Part id="pv-changes" title="Changes">
          <ProjectChangelog projectId={p.id} compact />
        </Part>
        {/* viko-call: calls and the actions they took, "via call" */}
        <Part id="pv-calls" title="Calls">
          <div className="pv-calls">
            <CallHistory projectId={p.id} />
          </div>
        </Part>
        <Part id="pv-import" title="Changes made outside Viko">
          <ImportReview project={p} />
        </Part>
      </div>
    </View>
  )
}

export function ProjectBuildsView() {
  const p = useTabProject()
  return (
    <View label={`${p.name} builds`}>
      <ProjectBuilds project={p} />
    </View>
  )
}

export function ProjectArtifactsView() {
  const p = useTabProject()
  return (
    <View label={`${p.name} artifacts`}>
      <div className="pv-body">
        <ArtifactGallery projectId={p.id} bare />
        <Part id="pv-research" title="Project research">
          <p className="pv-note">
            Research for {p.name}. Research you keep across projects is in the{' '}
            <Link to={settingsHref('research')}>Research library</Link>.
          </p>
          <ResearchLibrary projectId={p.id} />
        </Part>
        <Part id="pv-feedback" title="Feedback">
          <FeedbackQueue project={p} />
        </Part>
      </div>
    </View>
  )
}

export function ProjectSettingsView() {
  const p = useTabProject()
  const [adding, setAdding] = useState(false)
  return (
    <View label={`${p.name} settings`}>
      <div className="pv-body">
        <Part
          id="pv-lanes"
          title="Lanes"
          aside={
            <button type="button" className="pv-btn" onClick={() => setAdding(true)}>
              <Plus size={16} aria-hidden="true" />
              New lane
            </button>
          }
        >
          <p className="pv-note">
            Your requests become lanes on their own. Start one by hand when you already know the
            scope.
          </p>
          {adding ? <NewWorkstream project={p} onClose={() => setAdding(false)} /> : null}
        </Part>
        <Part id="pv-checkouts" title="Checkouts">
          <ProjectCheckouts project={p} />
        </Part>
        <ProjectPolicy project={p} />
        <Part id="pv-rules" title="Rules">
          <p className="pv-note">
            What agents working on {p.name} are told. Rules for every project are in{' '}
            <Link to={settingsHref('rules')}>Settings › Rules</Link>.
          </p>
          <RulesPage projectId={p.id} />
        </Part>
      </div>
    </View>
  )
}

/** Configuration, loop, autonomy, history consent and the project's other policies. */
function ProjectPolicy({ project }: { project: Project }) {
  const b = useBackend()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  return (
    <>
      <Part
        id="pv-config"
        title="Configuration"
        aside={
          <button type="button" className="pv-btn" onClick={() => setEditing(true)}>
            <FileCog size={16} aria-hidden="true" />
            Edit configuration
          </button>
        }
      >
        <p className="pv-note">
          {project.config ? (
            <>
              From <code className="pv-mono">viko.project.yaml</code>. The file in the repo stays
              authoritative; edits here change the hub’s copy, which steers routing and Home.
            </>
          ) : (
            <>
              The hub has no copy of this project’s{' '}
              <code className="pv-mono">viko.project.yaml</code> yet, so its policy can’t be edited
              here.
            </>
          )}
        </p>
        {editing ? (
          <ProjectConfigSheet
            key={b.artifactAccess.accountId}
            project={project}
            onClose={() => setEditing(false)}
            onSaved={async () => {
              await qc.invalidateQueries({ queryKey: qk.projects })
            }}
          />
        ) : null}
      </Part>
      <Part id="ps-loop" title="Loop profile">
        <LoopPicker project={project} />
      </Part>
      <Part id="ps-auto" title="Autonomy">
        <AutonomyEditor project={project} />
      </Part>
      <HistoryConsent project={project} />
      <div className="pv-legacy">
        <ProjectInventory project={project} />
        <FeedbackSettings project={project} />
        <IngestSources project={project} />
        <LandGrantCard project={project} />
      </div>
    </>
  )
}
